import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Box-set books grid with TWO explicit, non-conflicting modes, chosen by a
// segmented toggle so a single drag gesture never has to mean two things:
//
//   • Reorder (default) — SortableJS; drag a cover to rearrange, order saves.
//   • Combine — SortableJS off; drag one cover onto another to open the
//     "Mark as Duplicate" modal (the exact gesture the Books index uses).
//
// Because only one mode is active at a time, there are no velocity/dwell
// heuristics to misfire — the previous "hold for 3s" approach was unreliable.
// The chosen mode is remembered in localStorage so it survives the page
// reload that marking a duplicate triggers (handy when tidying several).
export default class extends Controller {
  static targets = ["grid", "reorderBtn", "combineBtn", "manageBtn", "hint", "manageActions", "moveModal", "moveSelect"]
  static values = {
    url: String,
    removeUrl: String,
    moveUrl: String,
    mode: { type: String, default: "reorder" }
  }

  STORAGE_KEY = "boxSetGridMode"

  connect() {
    this.mediaQuery = window.matchMedia("(min-width: 768px)")
    this._onMedia = this._onMedia.bind(this)
    this.mediaQuery.addEventListener("change", this._onMedia)

    const saved = this._readStored()
    if (["combine", "reorder", "manage"].includes(saved)) this.modeValue = saved

    this._onMedia()
  }

  disconnect() {
    this.mediaQuery?.removeEventListener("change", this._onMedia)
    this._teardownAll()
  }

  // Drag interactions are desktop-only (touch can't hover/drag covers).
  _onMedia() {
    if (this.mediaQuery.matches) this._applyMode()
    else this._teardownAll()
  }

  get gridEl() {
    return this.hasGridTarget ? this.gridTarget : this.element.querySelector(".book-grid")
  }

  // --- mode switching --------------------------------------------------------

  setReorder() { this._setMode("reorder") }
  setCombine() { this._setMode("combine") }
  setManage() { this._setMode("manage") }

  _setMode(mode) {
    this._writeStored(mode)
    this.modeValue = mode // triggers modeValueChanged
  }

  modeValueChanged() {
    this._reflectToggle()
    if (this.mediaQuery?.matches) this._applyMode()
  }

  _applyMode() {
    this._teardownAll()
    if (this.modeValue === "combine") this._setupCombine()
    else if (this.modeValue === "manage") this._showManageActions(true)
    else this._setupReorder()
    this._reflectToggle()
  }

  _reflectToggle() {
    const mode = this.modeValue
    if (this.hasReorderBtnTarget) this.reorderBtnTarget.setAttribute("aria-pressed", String(mode === "reorder"))
    if (this.hasCombineBtnTarget) this.combineBtnTarget.setAttribute("aria-pressed", String(mode === "combine"))
    if (this.hasManageBtnTarget) this.manageBtnTarget.setAttribute("aria-pressed", String(mode === "manage"))
    if (this.hasHintTarget) {
      this.hintTarget.textContent =
        mode === "combine" ? "Combine mode — drag one cover on top of another to mark it as a duplicate."
        : mode === "manage" ? "Remove / Move mode — take a book out of this set, or move it to another."
        : "Reorder mode — drag covers to rearrange them."
    }
  }

  // --- reorder mode (SortableJS) --------------------------------------------

  _setupReorder() {
    if (this.sortable) return
    this.sortable = Sortable.create(this.gridEl, {
      animation: 150,
      ghostClass: "opacity-50",
      onEnd: (evt) => {
        if (evt.oldIndex === evt.newIndex) return // no-op drag
        this._save(this._order())
      }
    })
  }

  // --- combine mode (native drag → duplicate modal) -------------------------
  //
  // Drag events bubble, so binding on each grid-item wrapper also catches a
  // drag that starts on the inner <a>. We pass the wrapper explicitly so the
  // source/target are always the [data-sortable-id] cards regardless of which
  // child element the pointer grabbed.
  _setupCombine() {
    this._combo = []
    this._cards().forEach((card) => {
      card.setAttribute("draggable", "true")
      const h = {
        card,
        start: (e) => this._comboStart(e, card),
        over: (e) => this._comboOver(e, card),
        leave: () => this._comboLeave(card),
        drop: (e) => this._comboDrop(e, card),
        end: () => this._comboEnd()
      }
      card.addEventListener("dragstart", h.start)
      card.addEventListener("dragover", h.over)
      card.addEventListener("dragleave", h.leave)
      card.addEventListener("drop", h.drop)
      card.addEventListener("dragend", h.end)
      this._combo.push(h)
    })
  }

  _comboStart(e, card) {
    this._dragSource = card
    e.dataTransfer.effectAllowed = "move"
    e.dataTransfer.setData("text/plain", card.dataset.bookId || "")
    card.classList.add("opacity-50")
  }

  _comboOver(e, card) {
    if (!this._dragSource || card === this._dragSource) return
    e.preventDefault() // allow the drop
    e.dataTransfer.dropEffect = "move"
    card.classList.add("ring-2", "ring-amber-400", "ring-offset-2", "rounded-lg")
  }

  _comboLeave(card) {
    card.classList.remove("ring-2", "ring-amber-400", "ring-offset-2", "rounded-lg")
  }

  _comboDrop(e, card) {
    e.preventDefault()
    card.classList.remove("ring-2", "ring-amber-400", "ring-offset-2", "rounded-lg")
    if (!this._dragSource || card === this._dragSource) return

    const ddc = this.application.getControllerForElementAndIdentifier(this.element, "book-drag-drop")
    if (!ddc || typeof ddc.openDuplicateModal !== "function") return
    ddc.openDuplicateModal(
      this._dragSource.dataset.bookId, this._dragSource.getAttribute("title"),
      card.dataset.bookId, card.getAttribute("title")
    )
  }

  _comboEnd() {
    this._cards().forEach((c) =>
      c.classList.remove("opacity-50", "ring-2", "ring-amber-400", "ring-offset-2", "rounded-lg")
    )
    this._dragSource = null
  }

  _teardownCombine() {
    if (!this._combo) return
    this._combo.forEach((h) => {
      h.card.removeAttribute("draggable")
      h.card.removeEventListener("dragstart", h.start)
      h.card.removeEventListener("dragover", h.over)
      h.card.removeEventListener("dragleave", h.leave)
      h.card.removeEventListener("drop", h.drop)
      h.card.removeEventListener("dragend", h.end)
      h.card.classList.remove("opacity-50", "ring-2", "ring-amber-400", "ring-offset-2", "rounded-lg")
    })
    this._combo = null
    this._dragSource = null
  }

  _teardownAll() {
    if (this.sortable) { this.sortable.destroy(); this.sortable = null }
    this._teardownCombine()
    this._showManageActions(false)
  }

  // --- manage mode (remove / move) ------------------------------------------

  _showManageActions(show) {
    if (!this.hasManageActionsTarget) return
    this.manageActionsTargets.forEach((el) => el.classList.toggle("hidden", !show))
  }

  async removeBook(event) {
    event.preventDefault()
    event.stopPropagation()
    const bookId = event.currentTarget.dataset.bookId
    if (!bookId || !this.removeUrlValue) return
    if (!window.confirm("Remove this book from the box set? The book itself is kept in your library.")) return
    try {
      const resp = await fetch(this.removeUrlValue, {
        method: "DELETE",
        headers: this._jsonHeaders(),
        body: JSON.stringify({ book_id: bookId })
      })
      if (resp.ok) {
        window.location.reload() // refresh the volume count + completion bar
      } else {
        console.error("Remove failed", await resp.text())
        alert("Couldn't remove that book.")
      }
    } catch (e) {
      console.error("Remove error", e)
    }
  }

  openMove(event) {
    event.preventDefault()
    event.stopPropagation()
    this._moveBookId = event.currentTarget.dataset.bookId
    if (this.hasMoveModalTarget) {
      this.moveModalTarget.classList.remove("hidden")
      document.body.style.overflow = "hidden"
    }
  }

  cancelMove() {
    if (this.hasMoveModalTarget) this.moveModalTarget.classList.add("hidden")
    document.body.style.overflow = ""
    this._moveBookId = null
  }

  moveBackdrop(event) {
    if (event.target === this.moveModalTarget) this.cancelMove()
  }

  async confirmMove() {
    if (!this._moveBookId || !this.hasMoveSelectTarget || !this.moveUrlValue) return
    const target = this.moveSelectTarget.value
    if (!target) return
    try {
      const resp = await fetch(this.moveUrlValue, {
        method: "PATCH",
        headers: this._jsonHeaders(),
        body: JSON.stringify({ book_id: this._moveBookId, target_box_set_id: target })
      })
      if (resp.ok) {
        window.location.reload()
      } else {
        console.error("Move failed", await resp.text())
        alert("Couldn't move that book.")
      }
    } catch (e) {
      console.error("Move error", e)
    }
  }

  _jsonHeaders() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
    }
  }

  // --- helpers ---------------------------------------------------------------

  _cards() {
    return Array.from(this.gridEl.querySelectorAll("[data-sortable-id]"))
  }

  _order() {
    return this._cards().map((c) => c.dataset.sortableId)
  }

  async _save(ids) {
    if (!this.urlValue) return
    try {
      await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ book_ids: ids })
      })
    } catch (error) {
      console.error("Failed to save order:", error)
    }
  }

  _readStored() {
    try { return localStorage.getItem(this.STORAGE_KEY) } catch { return null }
  }

  _writeStored(mode) {
    try { localStorage.setItem(this.STORAGE_KEY, mode) } catch { /* ignore */ }
  }
}
